// @ts-nocheck
import { useState, useRef, useMemo } from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { XGButton } from 'xg-cl';
import { getLineItemImageUrl } from '../../../utils/resourcePathHelper';
import { imageSize } from '../../../constants/files';
import {
  isSupportedImageFile,
  SUPPORTED_IMAGE_EXTENSIONS,
  getSupportedFormatsString
} from '../../../utils/supportedImages';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PhotoPickerModal from '../../Upload/PhotoPicker/PhotoPickerModal';
import { formatStockPhotoTitle } from '../../../services/photo/stockPhotoTitle';

/**
 * LineItemImageManager - Custom image manager that handles both existing images and new uploads
 *
 * This component:
 * 1. Displays existing images (with resourcepath) using direct img tags
 * 2. Handles new image uploads (File objects) using URL.createObjectURL
 * 3. Maintains the same UI/UX as TripDetailedDayServices
 */
const LineItemImageManager = ({
  images = [],
  onChange,
  onError,
  disabled = false,
  maxImages = 5,
  maxSize = 20 * 1024 * 1024, // 20MB default
  label = 'Add Image',
  stockEnabled = false,
  isLibrary = false
}) => {
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const { t } = useTranslation('common');
  const trip = useSelector((state) => state.trip);

  // Memoize image sources to prevent infinite re-renders
  const imageSources = useMemo(() => {
    return images.map((image) => {
      // If it's a File object (new upload), create object URL
      if (image instanceof File) {
        return {
          src: URL.createObjectURL(image),
          name: image.name,
          isFile: true
        };
      }

      if (image.stockPhoto) {
        return {
          src: image.stockPhoto.thumbnailUrl || image.stockPhoto.previewUrl,
          name:
            image.stockPhoto.title ??
            formatStockPhotoTitle(image.stockPhoto.altText),
          isFile: false,
          editable: true
        };
      }

      // If it's a database record with resourcepath, use the helper
      if (image.resourcepath) {
        const fileName = image.resourcepath.split('/').pop();
        return {
          src: getLineItemImageUrl({
            resourcePath: image.resourcepath,
            sizePrefix: imageSize.small.prefix
          }),
          name: image.imagename ?? fileName,
          isFile: false,
          isTitle:
            image.imagename != null &&
            !/\.(gif|jpe?g|png|webp)$/i.test(image.imagename)
        };
      }

      return {
        src: null,
        name: 'Image',
        isFile: false
      };
    });
  }, [images]);

  const handleAddClick = () => {
    if (stockEnabled) setPickerOpen(true);
    else setShowUploadArea(true);
  };

  const handleFileSelect = (event) => {
    const newFiles = Array.from(event.target.files);
    processFiles(newFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const newFiles = Array.from(e.dataTransfer.files);
    processFiles(newFiles);
  };

  const processFiles = (newFiles) => {
    let hasError = false;

    const validFiles = newFiles.filter((file) => {
      if (file.size > maxSize) {
        onError?.(
          `File too large. The maximum photo size is ${Math.floor(maxSize / (1024 * 1024))}MB. Please upload a smaller size.`
        );
        hasError = true;
        return false;
      }
      if (!isSupportedImageFile(file)) {
        onError?.(
          t('imageFormatNotSupported', {
            formats: getSupportedFormatsString()
          })
        );
        hasError = true;
        return false;
      }
      return true;
    });

    if (images.length + validFiles.length > maxImages) {
      onError?.(`You can only upload up to ${maxImages} images.`);
      return false;
    }

    if (!hasError && validFiles.length > 0) {
      const newImagesArray = [...images, ...validFiles];
      onChange(newImagesArray);
      setShowUploadArea(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return true;
    }
    return false;
  };

  const handleStockConfirm = (photos) => {
    if (images.length + photos.length > maxImages) return false;
    onChange([
      ...images,
      ...photos.map((stockPhoto) => ({
        stockPhoto: {
          ...stockPhoto,
          title: formatStockPhotoTitle(stockPhoto.altText)
        }
      }))
    ]);
    setPickerOpen(false);
    return true;
  };

  const handleStockTitleChange = (index, title) => {
    onChange(
      images.map((image, imageIndex) =>
        imageIndex === index
          ? { ...image, stockPhoto: { ...image.stockPhoto, title } }
          : image
      )
    );
  };

  const handlePickerUpload = (files) => {
    if (!processFiles(files)) return false;
    setPickerOpen(false);
    return true;
  };

  const handleDelete = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
  };

  const formatFileName = (fileName) => {
    if (!fileName.includes('.')) {
      return fileName.length > 13
        ? `${fileName.substring(0, 10)}...`
        : fileName;
    }
    const isLargeFileName = fileName.length >= 13;
    let substringLength = isLargeFileName ? 4 : 9;
    let fileNameShrinked = fileName.substring(0, substringLength);
    const fileExtension = fileName.split('.').pop();
    if (isLargeFileName) {
      fileNameShrinked += '...';
    }
    return `${fileNameShrinked}.${fileExtension}`;
  };

  return (
    <Box>
      {/* Add Image Button */}
      {!showUploadArea && (
        <Box>
          <XGButton
            id='add-image-button'
            text={label}
            type='secondary'
            size='large'
            variant='contained'
            onClick={handleAddClick}
            startIcon={<AddCircleIcon />}
            disabled={disabled || images.length >= maxImages}
          />
        </Box>
      )}

      {/* Upload Area */}
      {showUploadArea && (
        <Box
          sx={{
            border: isDragging ? '2px dashed #1976d2' : '2px dashed #ccc',
            borderRadius: '8px',
            padding: '32px',
            textAlign: 'center',
            backgroundColor: isDragging ? '#f0f7ff' : '#fafafa',
            mb: 2,
            cursor: 'pointer'
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type='file'
            accept={SUPPORTED_IMAGE_EXTENSIONS}
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={disabled}
          />
          <Typography variant='body1' sx={{ mb: 1 }}>
            Drag and drop or
          </Typography>
          <XGButton
            text='Upload Image'
            type='primary'
            size='medium'
            variant='contained'
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          />
          <Typography
            variant='caption'
            display='block'
            sx={{ mt: 2, color: '#666' }}
          >
            Max file size for photos is {Math.floor(maxSize / (1024 * 1024))}MB.
            Please resize if needed.
          </Typography>
        </Box>
      )}

      {/* Image Grid */}
      {imageSources.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            justifyContent: 'flex-start',
            marginTop: '16px'
          }}
        >
          {imageSources.map((imageSource, index) => {
            const { src, name, editable, isTitle } = imageSource;
            const displayName = isTitle ? name : formatFileName(name);

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  width: '110px',
                  minHeight: '150px',
                  justifyContent: 'end'
                }}
              >
                {src ? (
                  <img
                    src={src}
                    alt={name}
                    style={{
                      width: '100px',
                      height: '100px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      marginBottom: '8px'
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100px',
                      height: '100px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant='caption'>No Preview</Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    minHeight: '56px'
                  }}
                >
                  {editable ? (
                    <Tooltip title={name}>
                      <input
                        aria-label={t('title')}
                        value={name}
                        onChange={(event) =>
                          handleStockTitleChange(index, event.target.value)
                        }
                        disabled={disabled}
                        maxLength={500}
                        style={{
                          width: '100%',
                          minWidth: 0,
                          flex: 1,
                          border: 0,
                          background: 'transparent',
                          color: '#666',
                          fontSize: '12px',
                          padding: 0,
                          textOverflow: 'ellipsis'
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title={name}>
                      <Typography
                        variant='caption'
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}
                      >
                        {displayName}
                      </Typography>
                    </Tooltip>
                  )}
                  {!disabled && (
                    <Tooltip title='Delete'>
                      <IconButton
                        size='small'
                        onClick={() => handleDelete(index)}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      {stockEnabled && (
        <PhotoPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onConfirm={handleStockConfirm}
          onUploadConfirm={handlePickerUpload}
          destinationTerm={
            isLibrary
              ? ''
              : trip?.city?.cityname || trip?.country?.countryname || ''
          }
          tripTitle={isLibrary ? '' : trip?.tripname || ''}
          multiple
          remainingSlots={Math.max(0, maxImages - images.length)}
        />
      )}
    </Box>
  );
};

LineItemImageManager.propTypes = {
  images: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  disabled: PropTypes.bool,
  maxImages: PropTypes.number,
  maxSize: PropTypes.number,
  label: PropTypes.string,
  stockEnabled: PropTypes.bool,
  isLibrary: PropTypes.bool
};

export default LineItemImageManager;
